import Link from 'next/link';
import Skeleton from 'react-loading-skeleton';

import { CHAIN_IDS, NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '@/constants/chain';

import { ZoraToken } from '@/hooks/useZora';

import { Avatar } from '@/ui/Avatars/Avatar';
import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { varMappings } from '@/utils/generated/varMappings';
import { isZoraCreatorCoin } from '@/utils/zora';

interface ZoraCreatorInfoProps {
  activeToken: TokenResult;
  zora?: ZoraToken;
}

export const ZoraCreatorInfo = ({ activeToken, zora }: ZoraCreatorInfoProps) => {
  if (isZoraCreatorCoin(activeToken)) {
    return <div className="text-body-md text-token-gray-800 mt-2 ml-1">Creator on Zora</div>;
  }

  if (!zora) {
    return (
      <div className="mt-2">
        <Skeleton
          width={150}
          height={20}
          baseColor={varMappings['token-gray-200']}
          highlightColor={varMappings['token-gray-400']}
        />
      </div>
    );
  }

  if (!zora.profile) {
    return null;
  }

  const creatorName = zora.profile.displayName || zora.profile.username || 'Unknown';
  const avatarAlt = `${creatorName} avatar`;

  return (
    <div className="flex items-center text-body-md text-token-gray-800 mt-2 ml-1">
      <span>Created by</span>
      {zora.profile.creatorCoin?.address ? (
        <Link
          href={{
            pathname: '/tokens/[chainId]/[...contractAddresses]',
            query: {
              chainId: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[activeToken.chainId || CHAIN_IDS.BASE],
              contractAddresses: [zora.profile.creatorCoin.address],
            },
          }}
          className="flex items-center gap-1 ml-1 text-token-gray-1000 group"
        >
          <TokenAvatar
            address={zora.profile.creatorCoin.address}
            chainId={activeToken.chainId}
            size={16}
            alt={avatarAlt}
            showChainLogo={false}
            className="ml-1"
          />
          <span className="group-hover:underline">{creatorName}</span>
        </Link>
      ) : (
        <div className="flex items-center gap-1 ml-1">
          {(zora.profile.avatar?.medium || zora.profile.avatar?.small) && (
            <Avatar
              src={zora.profile.avatar.medium || zora.profile.avatar.small || ''}
              fallbackSrc="/images/chains/none.svg"
              size={16}
              alt={avatarAlt}
            />
          )}
          <span>{creatorName}</span>
        </div>
      )}
      <span className="ml-1">on Zora</span>
    </div>
  );
};
